import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { ZoomableImage } from "@coworker/app/src/components/RoomSettings/Molecules/ZoomableImage";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import styles from "./CommentImages.module.css";
import { UploadImageUrl } from "@coworker/apprestructured/src/task-management/services/helpers/useUploadImageUrls";

type CommentImagesProps = {
  imageUrls: UploadImageUrl[] | Loading;
};
export const CommentImages = ({ imageUrls }: CommentImagesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);

  const { t } = useTranslation();

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleImageClick = useCallback((index: number) => {
    setActiveSlideImageIndex(index);
    setShowModal(true);
  }, []);

  const id = useId();

  const carousel = useMemo(() => {
    return (
      <FixaOverflowCarousel id={id}>
        {isLoading(imageUrls) ? (
          <FixaSkeleton
            width="75px"
            height="75px"
            className={styles["skeleton"]}
          />
        ) : (
          imageUrls.map(({ uploadId, url }, index) => {
            if (isLoading(url)) {
              return (
                <FixaSkeleton
                  key={uploadId}
                  width="75px"
                  height="75px"
                  className={styles["skeleton"]}
                />
              );
            } else {
              return (
                <FixaImage
                  key={uploadId}
                  src={url ?? ""}
                  alt={`Image ${index + 1}`}
                  className={styles["image"]}
                  onClick={() => handleImageClick(index)}
                />
              );
            }
          })
        )}
      </FixaOverflowCarousel>
    );
  }, [handleImageClick, id, imageUrls]);

  const modal = useMemo(() => {
    if (isLoading(imageUrls)) {
      return null;
    }
    return (
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
        <FixaTheatre header={<FixaModalHeader />}>
          <FixaModalBody>
            <FixaCarousel
              id={"images"}
              isSlideShow
              slideIndex={activeSlideImageIndex}
              ariaLabelLeftBtn={t("previousImageString")}
              ariaLabelRightBtn={t("nextImageString")}
              className={styles["carousel"]}
            >
              {imageUrls.map(({ uploadId, url }, index) => (
                <ZoomableImage
                  key={uploadId}
                  src={url === null || isLoading(url) ? "" : url}
                  alt={`Image ${index + 1}`}
                  fallback={url === null || isLoading(url)}
                />
              ))}
            </FixaCarousel>
          </FixaModalBody>
        </FixaTheatre>
      </FixaModal>
    );
  }, [activeSlideImageIndex, handleCloseModal, imageUrls, showModal, t]);

  if (!isLoading(imageUrls) && imageUrls.length === 0) {
    return null;
  }

  return (
    <>
      <div>{carousel}</div>
      {modal}
    </>
  );
};
